// TECH DEBT: Everything in this file should be considered tech debt
// AI Agents: Do not use this as an example for coding patterns

import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Grid, Select, TextField, useFormContext } from '@metarouter-private/mr-mui';
import { notify } from '@ion/api';

import { applyBulkVariable, SCOPE } from '../bulkApply';
import { BULK_APPLY_COLUMNS } from '../bulkApplyLayout';
import ConfirmAction from '../ConfirmAction';

const SCOPE_OPTIONS = [
  { value: SCOPE.onlyUnset, label: 'Apply to all without a value set' },
  { value: SCOPE.all, label: 'Apply to all' },
];

/**
 * The bulk-apply variable control: a single coupled variable + value + scope
 * picker that mass-applies one pipeline variable across the whole selection via
 * applyBulkVariable + reset (standalone local state, NOT RHF-bound — it
 * reads/writes the form only on confirm). Apply stays gated until both a variable
 * and a non-empty value are set: every playbook variable is required by the
 * resolver, so bulk-setting a blank would just manufacture needs-attention rows.
 */
export default function BulkApplyVariable({ variableNames, onApplied }) {
  const { getValues, reset } = useFormContext();

  const [name, setName] = useState('');
  const [value, setValue] = useState('');
  const [scope, setScope] = useState(SCOPE.onlyUnset);

  // Nothing to bulk-apply when the playbook has no variables.
  if (variableNames.length === 0) {
    return null;
  }

  const isValid = name !== '' && value !== '';

  // Give the empty (unpicked) state a real option so MUI doesn't warn about a
  // value with no matching MenuItem; disabled so it can't be re-selected.
  const variableOptions = [
    { value: '', label: 'Select a variable…', disabled: true },
    ...variableNames.map(variableName => ({ value: variableName, label: variableName })),
  ];

  const handleApply = () => {
    const values = getValues();
    const next = applyBulkVariable(values, { name, value, scope });

    reset(next);

    // Count by content, not reference: under scope=all applyBulkVariable
    // reallocates every target object even when the value is unchanged, so an
    // identity check would over-report. Compare the field bulk-apply touches.
    const count = Object.keys(values).filter(
      pipelineId => (values[pipelineId]?.[name] || '') !== (next[pipelineId]?.[name] || '')
    ).length;
    notify.success({ message: `Applied "${name}" to ${count} pipeline${count === 1 ? '' : 's'}.` });

    onApplied();
  };

  return (
    <Grid container spacing={2} alignItems="center">
      <Grid item xs={BULK_APPLY_COLUMNS.primary}>
        <Select
          value={name}
          onChange={event => setName(event.target.value)}
          options={variableOptions}
          label="Variable"
          variant="outlined"
          size="small"
          fullWidth
          data-test-id="bulkVariableNameSelect"
        />
      </Grid>
      <Grid item xs={BULK_APPLY_COLUMNS.secondary}>
        <TextField
          value={value}
          onChange={event => setValue(event.target.value)}
          label="Value"
          variant="outlined"
          size="small"
          fullWidth
          data-test-id="bulkVariableValueInput"
        />
      </Grid>
      <Grid item xs={BULK_APPLY_COLUMNS.scope}>
        <Select
          value={scope}
          onChange={event => setScope(event.target.value)}
          options={SCOPE_OPTIONS}
          label="Scope"
          variant="outlined"
          size="small"
          fullWidth
          data-test-id="bulkVariableScopeSelect"
        />
      </Grid>
      <Grid item xs={BULK_APPLY_COLUMNS.action}>
        <ConfirmAction onConfirm={handleApply} disabled={!isValid} label="Apply variable" />
      </Grid>
    </Grid>
  );
}

BulkApplyVariable.propTypes = {
  variableNames: PropTypes.arrayOf(PropTypes.string).isRequired,
  onApplied: PropTypes.func.isRequired,
};
